/**
 * WsaReport - WSA-Auswertung (Waschschleuderautomat)
 * 
 * Zeigt:
 * - Zeitreihe kg/Tag
 * - Top 5 Maschinen
 * - Einträge-Liste
 * - Export
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Calendar,
  Download,
  Loader2,
  AlertTriangle,
  TrendingUp,
  WashingMachine,
  Info,
  Users,
  FileText,
  FileSpreadsheet,
  Pencil,
  X,
  Check,
  Filter,
  Eye,
  Clock,
  MessageSquareWarning,
} from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import { HorizontalBarChart } from '../components/BarChart';
import { Tooltip } from '../components/Tooltip';
import { ExportButtonsPlaceholder } from '../components/ExportButtonsPlaceholder';
import { getChartLabelForMachine, getChartLabelForCustomer, getChartLabelForProgram } from '../utils/chart-labels';
import {
  createOrUpdateOverride,
  removeOverride,
  WsaOverridePayload,
  CorrectionCategory,
  ApiException,
  getWsaCorrectionsSummary,
  CorrectionsSummaryResponse,
  resolveCorrectionRequestV2,
  ItemResolutionInput,
} from '../api/client';
import {
  getTodayBerlin,
  getQuickPickerRange,
  type QuickPicker,
} from '@wash-ops/shared';
import { formatBerlinDateDE, formatBerlinDateTimeDE } from '../utils/berlinDate';

type Preset = QuickPicker | 'custom';

/**
 * Formatiert einen YYYY-MM-DD String als DD.MM.YYYY
 */
function formatDateDE(dateStr: string): string {
  const [y, m, d] = dateStr.split('-');
  return `${d}.${m}.${y}`;
}

/**
 * Generiert ein konkretes Range-Label für die UI
 * Zeigt immer den effektiven Zeitraum mit Start–Ende
 */
function getRangeLabel(preset: Preset, startDate: string, endDate: string): string {
  const today = getTodayBerlin();
  const startDE = formatDateDE(startDate);
  const endDE = formatDateDE(endDate);

  switch (preset) {
    case 'today':
      return `${startDE} (bis jetzt)`;
    case 'yesterday':
    case 'dayBefore':
      return startDE;
    case 'thisWeek':
    case 'thisMonth':
      return `${startDE} \u2013 ${endDE} (bis heute)`;
    case 'lastWeek':
    case 'lastMonth':
      return `${startDE} \u2013 ${endDE}`;
    case 'custom':
      if (startDate === endDate) {
        return startDE;
      }
      if (endDate >= today) {
        return `${startDE} \u2013 ${endDE} (bis heute)`;
      }
      return `${startDE} \u2013 ${endDE}`;
    default:
      return `${startDE} \u2013 ${endDE}`;
  }
}

const PRESET_LABELS: Record<Preset, string> = {
  today: 'Heute',
  yesterday: 'Gestern',
  dayBefore: 'Vorgestern',
  thisWeek: 'Diese Woche',
  lastWeek: 'Letzte Woche',
  thisMonth: 'Dieser Monat',
  lastMonth: 'Letzter Monat',
  custom: 'Zeitraum wählen',
};

interface WsaEntry {
  id: string;
  weightKg: number;
  recordedAt: string;
  machine: { id: string; name: string };
  customer: { id: string; name: string; shortName: string | null };
  program: { id: string; number: string; name: string };
  recordedBy: { displayName: string };
  // Effective Values (Override > Original)
  effectiveWeightKg: number;
  effectiveCustomerId: string | null;
  effectiveMachineId: string;
  effectiveProgramId: string;
  // Shift: Original + Effective
  originalShiftDateStr: string;
  originalShiftTypeCodeSnap: string;
  effectiveShiftDateStr: string;
  effectiveShiftTypeCodeSnap: string;
  // Override Info
  isOverridden: boolean;
  isCorrected: boolean;
  overrideInfo: {
    reason: string;
    by: string;
    at: string;
  } | null;
  // Correction Requests
  openCorrectionCount: number;
  openCorrectionPreview: {
    id: string;
    category: CorrectionCategory;
    reasonText: string;
    createdAt: string;
    correctionItems?: Array<{ field: string; oldValue: unknown; newValue: unknown }> | null;
    workerComment?: string | null;
  } | null;
}

interface ChartData {
  id: string;
  name: string;
  value: number;
  count: number;
}

interface DayData {
  date: string;
  kg: number;
  count: number;
}

interface ShiftSummary {
  appliedDateField: 'recordedAt' | 'shiftDate';
  totals: {
    totalKg: number;
    byShiftType: { EARLY: number; LATE: number; SATURDAY: number };
    hbwKgByShiftType: { EARLY: number; LATE: number; SATURDAY: number };
    hbwKgTotal: number;
  };
  byShift: Array<{
    shiftDate: string;
    shiftType: string;
    kg: number;
    count: number;
    isOngoing: boolean;
    shiftStartBerlin: string | null;
    shiftEndBerlin: string | null;
  }>;
  asOfBerlin: string;
  hasOngoingShift: boolean;
}

const SHIFT_TYPE_LABELS: Record<string, string> = {
  EARLY: 'Frühschicht',
  LATE: 'Spätschicht',
  SATURDAY: 'Samstag',
};

const SHIFT_TYPE_COLORS: Record<string, string> = {
  EARLY: 'text-blue-600',
  LATE: 'text-green-600',
  SATURDAY: 'text-purple-600',
};

const SHIFT_TYPE_BADGE: Record<string, string> = {
  EARLY: 'inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-700',
  LATE: 'inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700',
  SATURDAY: 'inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-purple-100 text-purple-700',
};

const GRID_COLS_MAP: Record<number, string> = {
  1: 'grid-cols-1 lg:grid-cols-1',
  2: 'grid-cols-2 lg:grid-cols-2',
  3: 'grid-cols-2 lg:grid-cols-3',
  4: 'grid-cols-2 lg:grid-cols-4',
};

function getWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

export default function WsaReportPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // URL-State Persistence
  const initialPreset = (searchParams.get('preset') as Preset) || 'today';
  const [preset, setPreset] = useState<Preset>(
    ['today', 'yesterday', 'dayBefore', 'thisWeek', 'lastWeek', 'thisMonth', 'lastMonth', 'custom'].includes(initialPreset)
      ? initialPreset
      : 'today'
  );

  // Sync preset to URL
  useEffect(() => {
    if (preset !== 'custom') {
      setSearchParams({ preset }, { replace: true });
    }
  }, [preset, setSearchParams]);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const range = useMemo(() => {
    if (preset === 'custom') {
      if (customStart && customEnd) {
        return { start: customStart, end: customEnd };
      }
      const today = getTodayBerlin();
      return { start: today, end: today };
    }
    const r = getQuickPickerRange(preset);
    return { start: r.startDate, end: r.endDate };
  }, [preset, customStart, customEnd]);

  const fromDate = range.start;
  const toDate = range.end;

  // Visualisierungs-Modus basierend auf Preset
  const vizMode = useMemo(() => {
    if (preset === 'today' || preset === 'yesterday' || preset === 'dayBefore') {
      return 'single-day';
    }
    if (preset === 'thisWeek' || preset === 'lastWeek') {
      return 'week';
    }
    return 'month'; // thisMonth, lastMonth, custom
  }, [preset]);

  // Partial-Range Info: WARN vs INFO vs none
  // WARN: Zeitraum nicht abgeschlossen (thisWeek/thisMonth)
  // INFO: Intraday (today) - neutral, kein Warncharakter
  // none: Abgeschlossene Zeiträume
  const partialRangeInfo = useMemo(() => {
    const today = getTodayBerlin();

    // today = INFO (laufender Tag, neutral)
    if (preset === 'today') {
      return { type: 'info' as const, message: 'Stand: heute bis jetzt (laufender Tag)' };
    }

    // thisWeek/thisMonth = WARN (Zeitraum nicht abgeschlossen)
    if (preset === 'thisWeek' || preset === 'thisMonth') {
      return {
        type: 'warn' as const,
        message: `Zeitraum nicht abgeschlossen - Werte bis ${formatBerlinDateDE(today)} sind vorläufig.`
      };
    }

    // custom mit End >= today = WARN
    if (preset === 'custom' && customEnd && customEnd >= today) {
      return {
        type: 'warn' as const,
        message: `Zeitraum nicht abgeschlossen - Werte bis ${formatBerlinDateDE(today)} sind vorläufig.`
      };
    }

    // Abgeschlossene Zeiträume: kein Banner
    return { type: 'none' as const, message: '' };
  }, [preset, customEnd]);

  const isPartialRange = partialRangeInfo.type !== 'none';

  const applyPreset = (p: Preset) => {
    if (p !== 'custom') {
      setCustomStart('');
      setCustomEnd('');
    }
    setPreset(p);
  };

  const [entries, setEntries] = useState<WsaEntry[]>([]);
  const [byMachine, setByMachine] = useState<ChartData[]>([]);
  const [byCustomer, setByCustomer] = useState<ChartData[]>([]);
  const [byProgram, setByProgram] = useState<ChartData[]>([]);
  const [byDay, setByDay] = useState<DayData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [totalKg, setTotalKg] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  // Pagination State
  const [page, setPage] = useState(0);
  const [pageSize] = useState(50);
  const [displayedCount, setDisplayedCount] = useState(0);
  const isInitialMount = useRef(true);

  // Filter State
  const [onlyOpenCorrections, setOnlyOpenCorrections] = useState(false);

  // Shift Summary State (für Schicht-basierte UI)
  const [shiftSummary, setShiftSummary] = useState<ShiftSummary | null>(null);

  // Dynamisch: ShiftTypes die Daten haben ODER gerade laufen
  const presentShiftTypes = useMemo(() => {
    if (!shiftSummary?.byShift) return [];
    const typesWithData = new Set(
      shiftSummary.byShift
        .filter(s => s.count > 0 || s.isOngoing)
        .map(s => s.shiftType)
    );
    // Reihenfolge: EARLY, LATE, SATURDAY
    return ['EARLY', 'LATE', 'SATURDAY'].filter(t => typesWithData.has(t));
  }, [shiftSummary]);

  // Drilldown State: Ausgewählte Schicht für Entry-Filter
  const [selectedShiftDate, setSelectedShiftDate] = useState<string | null>(null);
  const [selectedShiftType, setSelectedShiftType] = useState<string | null>(null);

  const clearDrilldown = () => {
    setSelectedShiftDate(null);
    setSelectedShiftType(null);
    setPage(0);
  };

  const selectShift = (shiftDate: string, shiftType: string) => {
    setSelectedShiftDate(shiftDate);
    setSelectedShiftType(shiftType);
    setPage(0);
  };

  // Corrections Summary (Attention Area)
  const [correctionsSummary, setCorrectionsSummary] = useState<CorrectionsSummaryResponse | null>(null);

  // Correction Modal State
  const [correctionModal, setCorrectionModal] = useState<{
    open: boolean;
    entry: WsaEntry | null;
    mode: 'v2-resolve' | 'manual';
    saving: boolean;
  }>({ open: false, entry: null, mode: 'manual', saving: false });
  const [correctionForm, setCorrectionForm] = useState({
    weightKg: '',
    reason: '',
    closeRequest: false,
    shiftDate: '',
    shiftTypeCodeSnap: '',
    enableShiftOverride: false,
  });
  const [correctionError, setCorrectionError] = useState('');
  const [correctionSuccess, setCorrectionSuccess] = useState('');
  // V2: Pro-Feld Resolutions (ACCEPTED/REJECTED)
  const [v2Resolutions, setV2Resolutions] = useState<Record<string, 'ACCEPTED' | 'REJECTED'>>({});

  const openV2ResolveModal = (entry: WsaEntry) => {
    setCorrectionModal({ open: true, entry, mode: 'v2-resolve', saving: false });
    setCorrectionForm(f => ({ ...f, reason: '' }));
    const items = entry.openCorrectionPreview?.correctionItems;
    if (items && items.length > 0) {
      const initial: Record<string, 'ACCEPTED' | 'REJECTED'> = {};
      items.forEach(item => { initial[item.field] = 'ACCEPTED'; });
      setV2Resolutions(initial);
    } else {
      setV2Resolutions({});
    }
    setCorrectionError('');
    setCorrectionSuccess('');
  };

  const openManualCorrectionModal = (entry: WsaEntry) => {
    setCorrectionModal({ open: true, entry, mode: 'manual', saving: false });
    setCorrectionForm({
      weightKg: entry.effectiveWeightKg != null ? entry.effectiveWeightKg.toString() : '',
      reason: '',
      closeRequest: entry.openCorrectionCount > 0,
      shiftDate: entry.effectiveShiftDateStr ?? entry.originalShiftDateStr ?? '',
      shiftTypeCodeSnap: entry.effectiveShiftTypeCodeSnap ?? entry.originalShiftTypeCodeSnap ?? '',
      enableShiftOverride: false,
    });
    setV2Resolutions({});
    setCorrectionError(entry.effectiveWeightKg == null ? 'effectiveWeightKg fehlt im Server-Response' : '');
    setCorrectionSuccess('');
  };

  const handleCorrectionSubmit = async () => {
    if (!correctionModal.entry) return;
    
    const entry = correctionModal.entry;

    if (correctionModal.mode === 'manual') {
      const newWeight = parseFloat(correctionForm.weightKg);
      if (isNaN(newWeight) || newWeight < 0) {
        setCorrectionError('Gewicht muss eine Zahl >= 0 sein');
        return;
      }
      if (correctionForm.reason.trim().length < 5) {
        setCorrectionError('Begruendung muss mindestens 5 Zeichen haben');
        return;
      }
      if (correctionForm.enableShiftOverride && correctionForm.shiftDate) {
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(correctionForm.shiftDate)) {
          setCorrectionError('Schichtdatum muss im Format JJJJ-MM-TT sein');
          return;
        }
      }
    }

    setCorrectionModal(prev => ({ ...prev, saving: true }));
    setCorrectionError('');

    try {
      if (correctionModal.mode === 'v2-resolve' && entry.openCorrectionPreview) {
        const itemResolutions: ItemResolutionInput[] = Object.entries(v2Resolutions).map(([field, resolution]) => ({
          field,
          resolution,
        }));
        await resolveCorrectionRequestV2(entry.openCorrectionPreview.id, {
          itemResolutions,
          adminComment: correctionForm.reason.trim() || undefined,
        });
      } else {
        const newWeight = parseFloat(correctionForm.weightKg);
        const payload: WsaOverridePayload = {};
        if (newWeight !== entry.weightKg) {
          payload.weightKg = newWeight;
        }
        if (correctionForm.enableShiftOverride) {
          if (correctionForm.shiftDate && correctionForm.shiftDate !== entry.originalShiftDateStr) {
            payload.shiftDate = correctionForm.shiftDate;
          }
          if (correctionForm.shiftTypeCodeSnap && correctionForm.shiftTypeCodeSnap !== entry.originalShiftTypeCodeSnap) {
            payload.shiftTypeCodeSnap = correctionForm.shiftTypeCodeSnap;
          }
        }
        await createOrUpdateOverride({
          targetType: 'WSA_ENTRY',
          targetId: entry.id,
          overridePayload: payload,
          overrideReason: correctionForm.reason.trim(),
          correctionRequestId: correctionForm.closeRequest && entry.openCorrectionPreview
            ? entry.openCorrectionPreview.id
            : undefined,
        });
      }

      setCorrectionSuccess(correctionModal.mode === 'v2-resolve' ? 'Meldung bearbeitet' : 'Korrektur gespeichert');
      setTimeout(() => {
        setCorrectionModal({ open: false, entry: null, mode: 'manual', saving: false });
        loadData();
      }, 1000);
    } catch (err) {
      if (err instanceof ApiException) {
        setCorrectionError(err.data?.error || 'Fehler beim Speichern');
      } else {
        setCorrectionError('Fehler beim Speichern der Korrektur');
      }
      setCorrectionModal(prev => ({ ...prev, saving: false }));
    }
  };

  const handleRemoveOverride = async () => {
    if (!correctionModal.entry) return;
    
    const entry = correctionModal.entry;
    
    if (correctionForm.reason.trim().length < 5) {
      setCorrectionError('Begründung muss mindestens 5 Zeichen haben');
      return;
    }

    setCorrectionModal(prev => ({ ...prev, saving: true }));
    setCorrectionError('');

    try {
      await removeOverride('WSA_ENTRY', entry.id, correctionForm.reason.trim());
      setCorrectionSuccess('Korrektur zurückgesetzt');
      setTimeout(() => {
        setCorrectionModal({ open: false, entry: null, mode: 'manual', saving: false });
        loadData(); // Reload data
      }, 1000);
    } catch (err) {
      if (err instanceof ApiException) {
        setCorrectionError(err.data?.error || 'Fehler beim Zurücksetzen');
      } else {
        setCorrectionError('Fehler beim Zurücksetzen der Korrektur');
      }
      setCorrectionModal(prev => ({ ...prev, saving: false }));
    }
  };

  const loadData = async (resetPage = false) => {
    setLoading(true);
    setError('');

    const currentPage = resetPage ? 0 : page;
    if (resetPage) setPage(0);

    const skip = currentPage * pageSize;
    const openFilter = onlyOpenCorrections ? '&onlyOpenCorrections=true' : '';
    
    // Drilldown-Filter für Entries
    const drilldownFilter = selectedShiftDate && selectedShiftType 
      ? `&shiftDate=${selectedShiftDate}&shiftType=${selectedShiftType}` 
      : '';

    try {
      const [entriesRes, shiftSummaryRes, byMachineRes, byCustomerRes, byProgramRes, byDayRes, summaryData] = await Promise.all([
        fetch(`/api/reports/wsa?from=${fromDate}&to=${toDate}&take=${pageSize}&skip=${skip}${openFilter}${drilldownFilter}`, {
          credentials: 'include',
        }),
        fetch(`/api/reports/wsa/shift-summary?from=${fromDate}&to=${toDate}`, {
          credentials: 'include',
        }),
        fetch(`/api/reports/wsa/by-machine?from=${fromDate}&to=${toDate}`, {
          credentials: 'include',
        }),
        fetch(`/api/reports/wsa/by-customer?from=${fromDate}&to=${toDate}`, {
          credentials: 'include',
        }),
        fetch(`/api/reports/wsa/by-program?from=${fromDate}&to=${toDate}`, {
          credentials: 'include',
        }),
        fetch(`/api/reports/wsa/by-day?from=${fromDate}&to=${toDate}`, {
          credentials: 'include',
        }),
        getWsaCorrectionsSummary(fromDate, toDate),
      ]);

      if (entriesRes.ok) {
        const data = await entriesRes.json();
        setEntries(data?.entries ?? []);
        setTotalCount(data?.totalCount ?? 0);
        setDisplayedCount(data?.entries?.length ?? 0);
      }

      // Shift Summary für KPIs und Schicht-Tabelle (unabhängig von Drilldown)
      if (shiftSummaryRes.ok) {
        const summaryJson = await shiftSummaryRes.json();
        setShiftSummary(summaryJson);
        setTotalKg(summaryJson?.totals?.totalKg ?? 0);
      }

      setCorrectionsSummary(summaryData);

      if (byMachineRes.ok) {
        const machineData = await byMachineRes.json();
        setByMachine(Array.isArray(machineData) ? machineData : []);
      }
      if (byCustomerRes.ok) {
        const customerData = await byCustomerRes.json();
        setByCustomer(Array.isArray(customerData) ? customerData : []);
      }
      if (byProgramRes.ok) {
        const programData = await byProgramRes.json();
        setByProgram(Array.isArray(programData) ? programData : []);
      }
      if (byDayRes.ok) {
        const dayData = await byDayRes.json();
        setByDay(Array.isArray(dayData) ? dayData : []);
      }
    } catch {
      setError('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  // Bei Datum/Filter-Änderung: Page zurücksetzen + Drilldown löschen
  useEffect(() => {
    setSelectedShiftDate(null);
    setSelectedShiftType(null);
    loadData(true);
  }, [fromDate, toDate, onlyOpenCorrections]);

  // Bei Drilldown-Änderung: Entries neu laden
  useEffect(() => {
    loadData(true);
  }, [selectedShiftDate, selectedShiftType]);

  // Bei Page-Änderung: Nur Entries nachladen (nicht beim initialen Mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    loadData(false);
  }, [page]);

  const totalPages = Math.ceil(totalCount / pageSize);

  const handleExport = () => {
    const headers = ['Datum', 'Maschine', 'Kunde', 'Programm', 'Gewicht (kg)', 'Erfasst von'];
    const rows = entries.map((e) => [
      formatBerlinDateDE(e.recordedAt),
      e.machine?.name ?? '—',
      e.customer?.shortName || e.customer?.name || '—',
      e.program ? `${e.program.number ?? ''} - ${e.program.name ?? ''}` : '—',
      e.weightKg?.toString() ?? '—',
      e.recordedBy?.displayName ?? '—',
    ]);

    const csv = [headers, ...rows].map((row) => row.join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wsa-auswertung-${fromDate}-${toDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">WSA-Auswertung</h1>
            <p className="text-sm text-slate-500">Waschschleuderautomat</p>
          </div>
          <ExportButtonsPlaceholder />
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border space-y-4">
          {/* Preset Buttons */}
          <div className="flex flex-wrap gap-2">
            {(['today', 'yesterday', 'dayBefore', 'thisWeek', 'lastWeek', 'thisMonth', 'lastMonth'] as Preset[]).map((p) => (
              <button
                key={p}
                onClick={() => applyPreset(p)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${preset === p
                  ? 'bg-blue-600 text-white'
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
              >
                {PRESET_LABELS[p]}
              </button>
            ))}
          </div>
          {/* Custom Range */}
          <div className="flex flex-wrap gap-4 items-center">
            <Calendar className="w-5 h-5 text-gray-400" />
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={customStart || fromDate}
                onChange={(e) => {
                  setCustomStart(e.target.value);
                  setPreset('custom');
                }}
                className="input"
              />
              <span className="text-gray-400">bis</span>
              <input
                type="date"
                value={customEnd || toDate}
                onChange={(e) => {
                  setCustomEnd(e.target.value);
                  setPreset('custom');
                }}
                className="input"
              />
            </div>
            {/* Range Label */}
            <div className="flex items-center gap-2 text-sm text-slate-700 bg-slate-50 px-4 py-2.5 rounded-md border border-slate-200">
              <Calendar className="w-4 h-4 text-slate-400" />
              <span className="font-medium">{getRangeLabel(preset, fromDate, toDate)}</span>
            </div>
          </div>
        </div>

        {/* Hinweis für offene Zeiträume: WARN (amber) vs INFO (blue) */}
        {partialRangeInfo.type === 'warn' && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">Zeitraum nicht abgeschlossen</p>
              <p className="text-sm text-amber-700">{partialRangeInfo.message}</p>
            </div>
          </div>
        )}
        {partialRangeInfo.type === 'info' && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
            <p className="text-sm text-blue-700">{partialRangeInfo.message}</p>
          </div>
        )}

        {/* Drilldown Filter-Chip */}
        {(selectedShiftDate || selectedShiftType) && (
          <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
            <Filter className="w-4 h-4 text-blue-600" />
            <span className="text-sm text-blue-700">
              Filter: {formatBerlinDateDE(selectedShiftDate + 'T12:00:00Z')} {SHIFT_TYPE_LABELS[selectedShiftType || ''] || selectedShiftType}
            </span>
            <button onClick={clearDrilldown} className="ml-2 text-blue-600 hover:text-blue-800">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* KPI Cards: Gesamt + dynamische Schicht-Kacheln (nur vorhandene) */}
        <div className={`grid gap-4 ${GRID_COLS_MAP[1 + presentShiftTypes.length] || 'grid-cols-2 lg:grid-cols-4'}`}>
          <div className="card">
            <div className="flex items-center gap-2">
              <p className="text-sm text-gray-500">Gesamt</p>
              {(isPartialRange || shiftSummary?.hasOngoingShift) && (
                <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded" title="Zeitraum oder Schicht läuft noch">
                  <Clock className="w-3 h-3" />
                  vorläufig
                </span>
              )}
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {totalKg.toLocaleString('de-DE')} kg
            </p>
          </div>
          {presentShiftTypes.map(type => {
            const isTypeOngoing = shiftSummary?.byShift.some(s => s.shiftType === type && s.isOngoing) ?? false;
            const shiftKg = shiftSummary?.totals?.byShiftType?.[type as keyof typeof shiftSummary.totals.byShiftType] ?? 0;
            const hbwKg = shiftSummary?.totals?.hbwKgByShiftType?.[type as keyof typeof shiftSummary.totals.hbwKgByShiftType] ?? 0;
            return (
              <div key={type} className="card">
                <div className="flex items-center gap-2">
                  <span className={SHIFT_TYPE_BADGE[type] ?? 'inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-700'}>
                    {SHIFT_TYPE_LABELS[type]}
                  </span>
                  {isTypeOngoing && (
                    <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded" title="Schicht läuft noch – Werte können sich ändern">
                      <Clock className="w-3 h-3" />
                      vorläufig
                    </span>
                  )}
                </div>
                <p className={`text-2xl font-bold ${SHIFT_TYPE_COLORS[type]}`}>
                  {shiftKg.toLocaleString('de-DE')} kg
                </p>
                {hbwKg > 0 && (
                  <p className="text-xs text-slate-400 mt-0.5">
                    davon {hbwKg.toLocaleString('de-DE')} kg HBW
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
            <p className="text-gray-600">{error}</p>
          </div>
        ) : (
          <>
            {/* Wochen-Modus: Vertikales Balkendiagramm */}
            {vizMode === 'week' && byDay.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Tagesverlauf
                </h3>
                <div className="flex items-end justify-between gap-2 h-48 pt-4">
                  {(() => {
                    const maxKg = Math.max(...byDay.map(d => d.kg), 1);
                    return byDay.map((day) => {
                      const pct = (day.kg / maxKg) * 100;
                      const weekday = new Date(day.date + 'T12:00:00').toLocaleDateString('de-DE', { timeZone: 'Europe/Berlin', weekday: 'short' });
                      return (
                        <div key={day.date} className="flex-1 flex flex-col items-center gap-1">
                          <span className="text-xs font-medium text-gray-600">
                            {day.kg > 0 ? day.kg.toLocaleString('de-DE') : ''}
                          </span>
                          <div className="w-full flex justify-center">
                            <div
                              className="w-8 bg-blue-500 rounded-t transition-all"
                              style={{ height: `${Math.max(pct, 2)}%`, minHeight: day.kg > 0 ? '8px' : '2px' }}
                            />
                          </div>
                          <span className="text-xs text-gray-500">{weekday}</span>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            )}

            {/* Monats-Modus: Stacked Bar Chart nach Schicht */}
            {vizMode === 'month' && shiftSummary && shiftSummary.byShift.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Tagesverlauf nach Schicht
                  </h3>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-blue-500" />Frühschicht</span>
                    <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-green-500" />Spätschicht</span>
                    <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-purple-500" />Samstag</span>
                  </div>
                </div>
                {(() => {
                  const SHIFT_COLORS: Record<string, string> = { EARLY: '#3b82f6', LATE: '#22c55e', SATURDAY: '#a855f7' };
                  const dayMap = new Map<string, Record<string, number>>();
                  for (const row of shiftSummary.byShift) {
                    if (!dayMap.has(row.shiftDate)) dayMap.set(row.shiftDate, {});
                    const entry = dayMap.get(row.shiftDate)!;
                    entry[row.shiftType] = (entry[row.shiftType] ?? 0) + row.kg;
                  }
                  const days = Array.from(dayMap.entries()).sort((a, b) => a[0].localeCompare(b[0]));
                  const maxKg = Math.max(...days.map(([, shifts]) => Object.values(shifts).reduce((s, v) => s + v, 0)), 1);

                  return (
                    <div className="flex items-end gap-1 h-48 pt-4">
                      {days.map(([date, shifts]) => {
                        const totalKg = Object.values(shifts).reduce((s, v) => s + v, 0);
                        const weekday = new Date(date + 'T12:00:00').toLocaleDateString('de-DE', { timeZone: 'Europe/Berlin', weekday: 'short' });
                        const dayNum = new Date(date + 'T12:00:00').toLocaleDateString('de-DE', { timeZone: 'Europe/Berlin', day: '2-digit' });
                        const segments = (['EARLY', 'LATE', 'SATURDAY'] as const).filter(t => (shifts[t] ?? 0) > 0);
                        return (
                          <div key={date} className="flex-1 flex flex-col items-center gap-1 min-w-0" title={`${weekday} ${dayNum}: ${totalKg.toLocaleString('de-DE')} kg`}>
                            <span className="text-[10px] font-medium text-gray-500 truncate w-full text-center">
                              {totalKg > 0 ? totalKg.toLocaleString('de-DE') : ''}
                            </span>
                            <div className="w-full flex justify-center">
                              <div className="w-6 flex flex-col-reverse" style={{ height: `${Math.max((totalKg / maxKg) * 100, 2)}%`, minHeight: totalKg > 0 ? '8px' : '2px' }}>
                                {segments.map(type => {
                                  const segKg = shifts[type] ?? 0;
                                  const segPct = totalKg > 0 ? (segKg / totalKg) * 100 : 0;
                                  return (
                                    <div
                                      key={type}
                                      className={type === segments[0] ? 'rounded-t' : ''}
                                      style={{ height: `${segPct}%`, backgroundColor: SHIFT_COLORS[type], minHeight: '2px' }}
                                    />
                                  );
                                })}
                              </div>
                            </div>
                            <span className="text-[10px] text-gray-400">{weekday}</span>
                            <span className="text-[10px] text-gray-300">{dayNum}</span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Single-Day-Modus: Kein Diagramm */}
            {vizMode === 'single-day' && (
              <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 text-center">
                <p className="text-sm text-slate-500">Ein-Tages-Ansicht – kein Verlauf verfügbar</p>
              </div>
            )}

            {/* Top 10 Charts Grid - responsive: 1 Spalte auf small, 2 ab md, 3 ab xl */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              <div className="min-w-0">
                <HorizontalBarChart
                  data={byMachine.map(m => ({ name: getChartLabelForMachine(m.name), fullName: m.name, value: m.value, id: m.id }))}
                  title="Top 10 Maschinen (kg)"
                  unit="kg"
                  height={310}
                  maxItems={11}
                  emptyMessage="Keine Maschinen-Daten"
                />
              </div>
              <div className="min-w-0">
                <HorizontalBarChart
                  data={byCustomer.map(c => ({ name: getChartLabelForCustomer(c.name), fullName: c.name, value: c.value, id: c.id }))}
                  title="Top 10 Kunden (kg)"
                  unit="kg"
                  height={310}
                  maxItems={11}
                  emptyMessage="Keine Kunden-Daten"
                />
              </div>
              <div className="min-w-0">
                <HorizontalBarChart
                  data={byProgram.map(p => ({ name: getChartLabelForProgram(p.name), fullName: p.name, value: p.value, id: p.id }))}
                  title="Top 10 Programme (kg)"
                  unit="kg"
                  height={310}
                  maxItems={11}
                  emptyMessage="Keine Programm-Daten"
                />
              </div>
            </div>

            {/* Schicht-Tabelle mit Drilldown */}
            {shiftSummary && shiftSummary.byShift.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
                  <h3 className="text-sm font-medium text-gray-700">
                    Schichten ({shiftSummary.byShift.length})
                  </h3>
                  {(selectedShiftDate || selectedShiftType) && (
                    <button
                      onClick={clearDrilldown}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      Filter zurücksetzen
                    </button>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Datum</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Schicht</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Gewicht</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Einträge</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Aktion</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {shiftSummary.byShift.map((row) => {
                        const isSelected = selectedShiftDate === row.shiftDate && selectedShiftType === row.shiftType;
                        const shiftColor = row.shiftType === 'EARLY' ? 'text-blue-600' 
                          : row.shiftType === 'LATE' ? 'text-green-600' 
                          : 'text-purple-600';
                        return (
                          <tr
                            key={`${row.shiftDate}-${row.shiftType}`}
                            onClick={() => selectShift(row.shiftDate, row.shiftType)}
                            className={`cursor-pointer transition-colors ${
                              isSelected 
                                ? 'bg-blue-50 border-l-4 border-l-blue-500' 
                                : 'hover:bg-gray-50'
                            }`}
                          >
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {formatBerlinDateDE(row.shiftDate + 'T12:00:00Z')}
                            </td>
                            <td className={`px-4 py-3 text-sm font-medium ${shiftColor}`}>
                              {SHIFT_TYPE_LABELS[row.shiftType] || row.shiftType}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {row.isOngoing ? (
                                <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded" title="Schicht läuft noch – Werte können sich ändern">
                                  <Clock className="w-3 h-3" />
                                  läuft
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400">abgeschlossen</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                              {row.kg.toLocaleString('de-DE')} kg
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 text-right">
                              {row.count}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <button
                                onClick={(e) => { e.stopPropagation(); selectShift(row.shiftDate, row.shiftType); }}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded transition-colors"
                              >
                                <Eye className="w-3.5 h-3.5" />
                                Anzeigen
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Corrections Attention Area */}
            {correctionsSummary && correctionsSummary.openCount > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">
                        {correctionsSummary.openCount} Eintrag{correctionsSummary.openCount !== 1 ? 'e' : ''} mit offener Meldung
                      </p>
                      <p className="text-sm text-amber-700 mt-1">
                        {correctionsSummary.overriddenCount} Einträge wurden bereits korrigiert
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setOnlyOpenCorrections(!onlyOpenCorrections)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      onlyOpenCorrections
                        ? 'bg-amber-600 text-white'
                        : 'bg-white border border-amber-300 text-amber-700 hover:bg-amber-100'
                    }`}
                  >
                    {onlyOpenCorrections ? 'Alle anzeigen' : 'Nur gemeldete'}
                  </button>
                </div>
              </div>
            )}

            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-700">
                  Einträge ({displayedCount} von {totalCount})
                  {onlyOpenCorrections && <span className="ml-2 text-amber-600">(nur gemeldete)</span>}
                  {selectedShiftDate && selectedShiftType && (
                    <span className="ml-2 text-blue-600">
                      (Filter: {formatBerlinDateDE(selectedShiftDate + 'T12:00:00Z')} - {SHIFT_TYPE_LABELS[selectedShiftType] || selectedShiftType})
                    </span>
                  )}
                </h3>
                <div className="flex gap-2">
                  {(selectedShiftDate || selectedShiftType) && (
                    <button
                      onClick={clearDrilldown}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      Schicht-Filter löschen
                    </button>
                  )}
                  {onlyOpenCorrections && (
                    <button
                      onClick={() => setOnlyOpenCorrections(false)}
                      className="text-xs text-gray-500 hover:text-gray-700"
                    >
                      Korrektur-Filter löschen
                    </button>
                  )}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Datum</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Schicht</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Maschine</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kunde</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Programm</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Gewicht</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {entries.map((entry) => (
                      <tr key={entry.id}>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {formatBerlinDateTimeDE(entry.recordedAt)}
                        </td>
                        <td className={`px-4 py-3 text-sm font-medium ${SHIFT_TYPE_COLORS[entry.effectiveShiftTypeCodeSnap] || 'text-gray-500'}`}>
                          {SHIFT_TYPE_LABELS[entry.effectiveShiftTypeCodeSnap] || entry.effectiveShiftTypeCodeSnap || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">{entry.machine?.name ?? '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {entry.customer?.shortName || entry.customer?.name || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {entry.program ? `${entry.program.number ?? ''} - ${entry.program.name ?? ''}` : '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                          <div className="flex items-center justify-end gap-2">
                            {entry.effectiveWeightKg != null ? (
                              <>
                                <span>{entry.effectiveWeightKg.toLocaleString('de-DE')} kg</span>
                                {entry.isCorrected && entry.effectiveWeightKg !== entry.weightKg && (
                                  <Tooltip content={`Original: ${entry.weightKg.toLocaleString('de-DE')} kg`}>
                                    <span className="text-xs text-purple-600">(korr.)</span>
                                  </Tooltip>
                                )}
                              </>
                            ) : (
                              <Tooltip content="effectiveWeightKg fehlt im Server-Response">
                                <span className="text-red-500">—</span>
                              </Tooltip>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            {entry.openCorrectionCount > 0 && (
                              <Tooltip
                                content={
                                  <div className="text-xs space-y-1">
                                    <p className="font-medium">Offene Meldung</p>
                                    {entry.openCorrectionPreview && (
                                      <>
                                        {entry.openCorrectionPreview.correctionItems ? (
                                          <div className="space-y-0.5">
                                            {entry.openCorrectionPreview.correctionItems.map((item: { field: string; newValue: unknown }) => {
                                              const labels: Record<string, string> = {
                                                lineId: 'Strecke', subLineId: 'Substrecke', customerId: 'Kunde',
                                                customerGroupId: 'Kundengruppe', weightKg: 'Gewicht',
                                                laundryClassId: 'Waescheklasse', articleGroupId: 'Artikelgruppe',
                                                machineId: 'Maschine', programId: 'Programm',
                                              };
                                              const label = labels[item.field] || item.field;
                                              const isWeight = item.field === 'weightKg';
                                              return (
                                                <p key={item.field}>
                                                  {label}: {isWeight ? `${String(item.newValue)} kg` : 'geaendert'}
                                                </p>
                                              );
                                            })}
                                          </div>
                                        ) : (
                                          <p>Kategorie: {entry.openCorrectionPreview.category}</p>
                                        )}
                                        {entry.openCorrectionPreview.workerComment && (
                                          <p>Grund: {entry.openCorrectionPreview.workerComment}</p>
                                        )}
                                        {!entry.openCorrectionPreview.workerComment && entry.openCorrectionPreview.reasonText && (
                                          <p>Grund: {entry.openCorrectionPreview.reasonText}</p>
                                        )}
                                        <p className="text-gray-400">Am: {new Date(entry.openCorrectionPreview.createdAt).toLocaleString('de-DE')}</p>
                                      </>
                                    )}
                                  </div>
                                }
                              >
                                <span
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded"
                                >
                                  <AlertTriangle className="w-3 h-3" />
                                  Gemeldet
                                </span>
                              </Tooltip>
                            )}
                            {entry.isCorrected && (
                              <Tooltip
                                content={
                                  <div className="text-xs">
                                    <p className="font-medium">Manuell korrigiert</p>
                                    {entry.overrideInfo && (
                                      <>
                                        <p>Grund: {entry.overrideInfo.reason}</p>
                                        <p>Von: {entry.overrideInfo.by}</p>
                                        <p>Am: {new Date(entry.overrideInfo.at).toLocaleString('de-DE')}</p>
                                      </>
                                    )}
                                  </div>
                                }
                              >
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">
                                  <Check className="w-3 h-3" />
                                  Korrigiert
                                </span>
                              </Tooltip>
                            )}
                            {!entry.openCorrectionCount && !entry.isCorrected && (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            {entry.openCorrectionCount > 0 && (
                              <Tooltip content="Korrekturanfrage bearbeiten">
                                <button
                                  onClick={() => openV2ResolveModal(entry)}
                                  className="p-1.5 text-amber-600 hover:text-amber-800 hover:bg-amber-50 rounded transition-colors"
                                  aria-label="Korrekturanfrage bearbeiten"
                                >
                                  <MessageSquareWarning className="w-4 h-4" />
                                </button>
                              </Tooltip>
                            )}
                            <Tooltip content="Korrigieren">
                              <button
                                onClick={() => openManualCorrectionModal(entry)}
                                className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                                aria-label="Korrigieren"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                            </Tooltip>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="px-4 py-3 border-t bg-gray-50 flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                      Seite {page + 1} von {totalPages}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                        disabled={page === 0}
                        className="px-3 py-1.5 text-sm bg-white border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Zurück
                      </button>
                      <button
                        onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                        disabled={page >= totalPages - 1}
                        className="px-3 py-1.5 text-sm bg-white border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Weiter
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Korrektur-Modal */}
      {correctionModal.open && correctionModal.entry && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">{correctionModal.mode === 'v2-resolve' ? 'Meldung bearbeiten' : 'Eintrag korrigieren'}</h3>
              <button
                onClick={() => setCorrectionModal({ open: false, entry: null, mode: 'manual', saving: false })}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              {/* Kontext */}
              <div className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
                <div><strong>Maschine:</strong> {correctionModal.entry.machine?.name ?? '—'}</div>
                <div><strong>Kunde:</strong> {correctionModal.entry.customer?.shortName || correctionModal.entry.customer?.name || '—'}</div>
                <div><strong>Programm:</strong> {correctionModal.entry.program ? `${correctionModal.entry.program.number} - ${correctionModal.entry.program.name}` : '—'}</div>
                <div><strong>Erfasst:</strong> {formatBerlinDateTimeDE(correctionModal.entry.recordedAt)}</div>
                <div><strong>Original-Gewicht:</strong> {correctionModal.entry.weightKg.toLocaleString('de-DE')} kg</div>
              </div>

              {/* === V2 RESOLVE MODE === */}
              {correctionModal.mode === 'v2-resolve' && correctionModal.entry.openCorrectionPreview?.correctionItems && (
                <>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                    <div className="flex items-center gap-2 text-amber-700 font-medium mb-2">
                      <AlertTriangle className="w-4 h-4" />
                      Gemeldete Korrekturen
                    </div>
                    <div className="space-y-2">
                      {correctionModal.entry.openCorrectionPreview.correctionItems.map((item: { field: string; oldValue: unknown; newValue: unknown }) => {
                        const labels: Record<string, string> = {
                          lineId: 'Strecke', subLineId: 'Substrecke', customerId: 'Kunde',
                          customerGroupId: 'Kundengruppe', weightKg: 'Gewicht',
                          laundryClassId: 'Waescheklasse', articleGroupId: 'Artikelgruppe',
                          machineId: 'Maschine', programId: 'Programm',
                        };
                        const label = labels[item.field] || item.field;
                        const isWeight = item.field === 'weightKg';
                        const resolution = v2Resolutions[item.field] || 'ACCEPTED';
                        return (
                          <div key={item.field} className={`flex items-center justify-between p-2 rounded border ${
                            resolution === 'ACCEPTED' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                          }`}>
                            <div className="text-xs">
                              <span className="font-medium">{label}:</span>{' '}
                              {isWeight ? (
                                <span>{String(item.oldValue)} → <strong>{String(item.newValue)} kg</strong></span>
                              ) : (
                                <span className="text-amber-600">geaendert</span>
                              )}
                            </div>
                            <div className="flex gap-1">
                              <button
                                type="button"
                                onClick={() => setV2Resolutions(prev => ({ ...prev, [item.field]: 'ACCEPTED' }))}
                                className={`px-2 py-0.5 text-xs rounded transition-colors ${
                                  resolution === 'ACCEPTED'
                                    ? 'bg-green-600 text-white'
                                    : 'bg-white border border-gray-300 text-gray-600 hover:bg-green-50'
                                }`}
                              >
                                Annehmen
                              </button>
                              <button
                                type="button"
                                onClick={() => setV2Resolutions(prev => ({ ...prev, [item.field]: 'REJECTED' }))}
                                className={`px-2 py-0.5 text-xs rounded transition-colors ${
                                  resolution === 'REJECTED'
                                    ? 'bg-red-600 text-white'
                                    : 'bg-white border border-gray-300 text-gray-600 hover:bg-red-50'
                                }`}
                              >
                                Ablehnen
                              </button>
                            </div>
                          </div>
                        );
                      })}
                      {correctionModal.entry.openCorrectionPreview.workerComment && (
                        <div className="text-xs text-amber-600 mt-1">
                          Grund: {correctionModal.entry.openCorrectionPreview.workerComment}
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Admin-Kommentar (optional)</label>
                    <textarea
                      value={correctionForm.reason}
                      onChange={(e) => setCorrectionForm(f => ({ ...f, reason: e.target.value }))}
                      placeholder="Optionaler Kommentar zur Entscheidung"
                      rows={2}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                    />
                  </div>
                </>
              )}

              {/* === MANUAL MODE === */}
              {correctionModal.mode === 'manual' && (
                <>
                  {/* Offene Meldung Info (Legacy) */}
                  {correctionModal.entry.openCorrectionCount > 0 && correctionModal.entry.openCorrectionPreview && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                      <div className="flex items-center gap-2 text-amber-700 font-medium mb-1">
                        <AlertTriangle className="w-4 h-4" />
                        Offene Meldung vorhanden
                      </div>
                      <div className="text-amber-600">
                        <div>Kategorie: {correctionModal.entry.openCorrectionPreview.category}</div>
                        <div>Grund: {correctionModal.entry.openCorrectionPreview.reasonText || correctionModal.entry.openCorrectionPreview.workerComment}</div>
                      </div>
                    </div>
                  )}

                  {/* Gewicht */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Gewicht (kg)</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={correctionForm.weightKg}
                      onChange={(e) => setCorrectionForm(f => ({ ...f, weightKg: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Schicht-Override Toggle */}
                  <div className="border-t pt-4">
                    <div className="flex items-center gap-2 mb-3">
                      <input
                        type="checkbox"
                        id="enableShiftOverride"
                        checked={correctionForm.enableShiftOverride}
                        onChange={(e) => setCorrectionForm(f => ({ ...f, enableShiftOverride: e.target.checked }))}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="enableShiftOverride" className="text-sm font-medium text-gray-700">
                        Schicht manuell korrigieren
                      </label>
                    </div>
                    {correctionForm.enableShiftOverride && (
                      <div className="space-y-3 pl-6 border-l-2 border-blue-200">
                        <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">
                          <div>Original: {correctionModal.entry.originalShiftDateStr} / {correctionModal.entry.originalShiftTypeCodeSnap}</div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Schichtdatum</label>
                          <input
                            type="date"
                            value={correctionForm.shiftDate}
                            onChange={(e) => setCorrectionForm(f => ({ ...f, shiftDate: e.target.value }))}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Schichttyp</label>
                          <select
                            value={correctionForm.shiftTypeCodeSnap}
                            onChange={(e) => setCorrectionForm(f => ({ ...f, shiftTypeCodeSnap: e.target.value }))}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="EARLY">Fruehschicht</option>
                            <option value="LATE">Spaetschicht</option>
                            <option value="SATURDAY">Samstag</option>
                          </select>
                        </div>
                        {(correctionForm.shiftDate !== correctionModal.entry.originalShiftDateStr ||
                          correctionForm.shiftTypeCodeSnap !== correctionModal.entry.originalShiftTypeCodeSnap) && (
                          <div className="text-xs text-blue-600 bg-blue-50 rounded p-2">
                            Neue Zuordnung: {correctionForm.shiftDate} / {correctionForm.shiftTypeCodeSnap}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Begruendung */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Begruendung <span className="text-red-500">*</span>
                      </label>
                      <span className={`text-xs font-medium ${correctionForm.reason.trim().length >= 5 ? 'text-green-600' : 'text-gray-400'}`}>
                        {correctionForm.reason.trim().length}/5
                      </span>
                    </div>
                    <textarea
                      value={correctionForm.reason}
                      onChange={(e) => setCorrectionForm(f => ({ ...f, reason: e.target.value }))}
                      placeholder="Warum wird der Eintrag korrigiert?"
                      rows={3}
                      aria-describedby="reason-error"
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none ${
                        correctionForm.reason.trim().length > 0 && correctionForm.reason.trim().length < 5 
                          ? 'border-amber-400' 
                          : ''
                      }`}
                    />
                    {correctionForm.reason.trim().length < 5 && (
                      <p id="reason-error" className="text-xs text-amber-600 mt-1">
                        Mindestens 5 Zeichen erforderlich
                      </p>
                    )}
                  </div>

                  {/* Meldung abschliessen Toggle */}
                  {correctionModal.entry.openCorrectionCount > 0 && (
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="closeRequest"
                        checked={correctionForm.closeRequest}
                        onChange={(e) => setCorrectionForm(f => ({ ...f, closeRequest: e.target.checked }))}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="closeRequest" className="text-sm text-gray-700">
                        Offene Meldung als erledigt markieren
                      </label>
                    </div>
                  )}
                </>
              )}

              {/* Error/Success */}
              {correctionError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {correctionError}
                </div>
              )}
              {correctionSuccess && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  {correctionSuccess}
                </div>
              )}
            </div>
            <div className="flex justify-between gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              <div>
                {correctionModal.mode === 'manual' && correctionModal.entry?.isOverridden && (
                  <button
                    onClick={handleRemoveOverride}
                    disabled={correctionModal.saving || correctionForm.reason.trim().length < 5}
                    className="px-4 py-2 text-sm text-red-600 hover:bg-red-50 border border-red-200 rounded-lg transition-colors disabled:text-gray-400 disabled:border-gray-200 disabled:cursor-not-allowed"
                  >
                    Korrektur zuruecksetzen
                  </button>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setCorrectionModal({ open: false, entry: null, mode: 'manual', saving: false })}
                  disabled={correctionModal.saving}
                  className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleCorrectionSubmit}
                  disabled={correctionModal.saving || (correctionModal.mode === 'manual' && correctionForm.reason.trim().length < 5)}
                  className="px-4 py-2 text-sm bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {correctionModal.saving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Speichern...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      {correctionModal.mode === 'v2-resolve' ? 'Meldung bearbeiten' : 'Korrektur speichern'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
